import React from 'react';
import getNewGUIDString from '../../util/GUID';
import { MessageShape } from '../../model/AppUIShapes';
import { useAppStore } from '../appStore';

/**
 * App Store同步Actions接口
 */
export interface AppStoreActions {
  setLanguage: (language: string) => void;
  changeCurrentListPanel: (panel: string) => void;
  changeCurrentViewPanel: (panel: string) => void;
  changeCurrentLanguage: (language: string) => void;
  addMessage: (message: MessageShape) => void;
  removeMessageFromLocal: (messageId: string) => void;
  switchToEditView: () => void;
  switchToShareView: () => void;
  appendMessageToLocal: (message: MessageShape) => void;
  setCurrentMuiLanguage: (language: string) => void;
  setCurrentView: (view: string) => void;
  setTheme: (theme: string) => void;
  setIsLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
}

/**
 * App Store异步Actions接口
 */
export interface AppAsyncActions {
  appendMessageAsync: (param: { messageType: number; message: string }) => Promise<number>;
  initialDataFetch: () => void;
}

/**
 * App Store Actions Hook - 用于在组件中访问store actions
 */
export const useAppStoreActions = () => {
  const setLanguage = useAppStore((state) => state.setLanguage);
  const changeCurrentListPanel = useAppStore((state) => state.changeCurrentListPanel);
  const changeCurrentViewPanel = useAppStore((state) => state.changeCurrentViewPanel);
  const changeCurrentLanguage = useAppStore((state) => state.changeCurrentLanguage);
  const addMessage = useAppStore((state) => state.addMessage);
  const removeMessageFromLocal = useAppStore((state) => state.removeMessageFromLocal);
  const switchToEditView = useAppStore((state) => state.switchToEditView);
  const switchToShareView = useAppStore((state) => state.switchToShareView);
  const appendMessageToLocal = useAppStore((state) => state.appendMessageToLocal);
  const setCurrentMuiLanguage = useAppStore((state) => state.setCurrentMuiLanguage);
  const setCurrentView = useAppStore((state) => state.setCurrentView);
  const setTheme = useAppStore((state) => state.setTheme);
  const setIsLoading = useAppStore((state) => state.setIsLoading);
  const setError = useAppStore((state) => state.setError);
  const clearError = useAppStore((state) => state.clearError);

  return React.useMemo(() => ({
    setLanguage,
    changeCurrentListPanel,
    changeCurrentViewPanel,
    changeCurrentLanguage,
    addMessage,
    removeMessageFromLocal,
    switchToEditView,
    switchToShareView,
    appendMessageToLocal,
    setCurrentMuiLanguage,
    setCurrentView,
    setTheme,
    setIsLoading,
    setError,
    clearError
  }), [
    setLanguage,
    changeCurrentListPanel,
    changeCurrentViewPanel,
    changeCurrentLanguage,
    addMessage,
    removeMessageFromLocal,
    switchToEditView,
    switchToShareView,
    appendMessageToLocal,
    setCurrentMuiLanguage,
    setCurrentView,
    setTheme,
    setIsLoading,
    setError,
    clearError
  ]);
};

// 实现App异步操作
export const appAsyncActions = (set: any, get: any): AppAsyncActions => ({
  /**
   * 异步添加消息到系统
   * @param param 消息参数
   * @returns 操作结果代码
   */
  appendMessageAsync: async (param: { messageType: number; message: string }): Promise<number> => {
    try {
      // 创建消息对象
      const message: MessageShape = {
        guid: getNewGUIDString(),
        messageType: param.messageType,
        message: param.message
      };
      
      // 这里将来可以添加API调用逻辑
      // 例如: await someApiService.addMessageToServer(message);
      
      // 返回成功代码
      return 0;
    } catch (error) {
      throw error;
    }
  },

  /**
   * 应用启动时数据预加载
   * 触发sections、groups、memos、memo indexes数据的远程获取
   */
  initialDataFetch: () => {
    // 这里只需要触发React Query的查询，不需要处理返回数据
    // React Query会自动处理缓存和状态管理
    // 具体的查询逻辑在各自的manager hooks中实现
    // 这个函数的主要作用是统一管理应用启动时的数据预加载
  }
});

// 为了向后兼容，保留旧的函数名
export const appStoreActions = useAppStoreActions;